// sessions-pipeline.js — PIPELINE sessions. Approved + sequenced, not yet started.
// Imported by sessions.js thin aggregator. Never import directly in components.
// To promote: move session from here to sessions-active.js when work begins.
// Last updated: May 31, 2026 — KEY DECISIONS corrected · OCI-10 status fixed · oci-outcome-detection removed (now in active.js)
//
// KEY DECISIONS (see PRODUCT_STRATEGY.md for full detail):
//   OCI NoSQL: stooq OHLCV ONLY (R29 internal/D3 · R40 3-table hard limit)
//   ATP-2 MP_ARCHIVE: AI training corpus · outcome labels · trade_log (permanent)
//   Independent pipelines: R34 — each self-contained, no shared engine
//   Broker adapter: R33 — brokerAdapter.placeOrder() only, never direct IBKR calls
//   VM-2: private subnet from day one at OCI-8 (not migrated later)
//   D3-PRIVATE-REPO: D3 execution code separate private repo, never in main repo


export const PIPELINE_SESSIONS = [

  // ─── OCI INFRASTRUCTURE (updated May 2026) ───────────────────────────────

  {
    id: 'oci-7-monitoring',
    sessionNumber: 'OCI-7',
    name: 'OCI-7: Monitoring + Alerts + Emergency Stop',
    status: 'planned',
    phase: 'Phase 8 — D3 Foundation',
    items: [
      'OCI Monitoring configured: VM-1 CPU, VM-2 CPU, ATP storage usage',
      'OCI Notifications SMS (free 0-100/month Zone 1 = US):',
        'D3 engine down > 5 minutes → SMS to owner phone',
        'IB Gateway disconnected → SMS immediately',
        'Layer 5 override fired (trade blocked) → SMS with reason',
        'Emergency stop triggered → SMS confirmation',
        'ATP storage > 18GB → SMS warning',
      'Emergency stop webhook on VM-2: listens on dedicated port',
        'One authenticated POST → cancel all orders + close all positions + disable D3',
        'TESTED before D3c — not documented, TESTED',
      'OCI Vault: all API keys stored, zero in environment files',
    ],
    prereqs: ['OCI-2 complete', 'OCI-3 complete'],
    doD: ['All 5 SMS alerts firing correctly', 'Emergency stop webhook tested', 'Vault holding all secrets'],
    smokeChecks: [],
    files: ['ARCHITECTURE.md'],
    vendorDeps: [],
  },

  {
    id: 'oci-8-d3-shadow',
    sessionNumber: 'OCI-8',
    name: 'OCI-8: D3 Engine Shadow Mode — All Layers Logging, No Execution',
    status: 'planned',
    phase: 'Phase 8 — D3 Foundation',
    items: [
      'D3 engine reads ATP signals every 5min during market hours',
      'Full 7-layer LLM evaluation runs: L1→L2→L3→L4→L5 decision chain',
      'All decisions logged to ATP with full reasoning chain',
      'Zero execution — IB Gateway connected but D3 never calls place_order()',
      'Counterfactual logging: what D3 would have done vs what market did',
      'Layer divergence logged: when L4 overrides L3 or L5 holds L4-confirmed trade',
      'This divergence data is training material for L6 weekly improvement',
      'D3a gate: paper accuracy > 65% over 30 days → D3b approved',
    ],
    prereqs: ['OCI-3 complete', 'OCI-7 complete', 'D0 manual trading 60 days positive Sharpe'],
    doD: ['All 7 layers firing', 'Decisions logged to ATP', 'Performance dashboard showing shadow results'],
    smokeChecks: [],
    files: ['ARCHITECTURE.md'],
    vendorDeps: [],
  },

  {
    id: 'oci-9-d3a-shadow-run',
    sessionNumber: 'OCI-9',
    name: 'OCI-9: D3a — 30-Day Shadow Mode Run',
    status: 'planned',
    phase: 'Phase 9 — D3 Shadow',
    items: [
      '30 days of shadow mode: all layers logging, zero execution',
      'Paper accuracy tracked daily: what D3 recommended vs what market did',
      'Weekly L6 self-improvement: Llama 4 Maverick adjusts signal weights each Sunday',
      'Gate: paper accuracy > 65% average over 30 days → D3b gate passed',
      'Performance dashboard readable by owner — manual vs D3 side-by-side',
      'Any week with accuracy < 50% → pause, analyse, adjust before continuing',
    ],
    prereqs: ['OCI-8 complete'],
    doD: ['30 days logged', 'Paper accuracy > 65%', 'L6 has run at least 4 times', 'D3b session approved'],
    smokeChecks: [],
    files: [],
    vendorDeps: [],
  },

  {
    id: 'oci-10-railway-migration',
    sessionNumber: 'OCI-10',
    name: 'OCI-10: Railway → VM-1 Migration — COMPLETE (May 4, 2026)',
    status: 'done',
    date: 'May 4, 2026',
    phase: 'Phase 3 — OCI Infrastructure',
    items: [
      'DNS cutover: Cloudflare backend URL updated from Railway to VM-1 OCI public IP',
      'MongoDB Atlas connection strings updated to VM-1 environment',
      'All environment variables moved from Railway dashboard to OCI Vault',
      'Smoke test full suite against OCI backend — 125/125 green',
      'Monitor OCI 48hrs: watch for any edge cases Railway was handling',
      'Railway service terminated — saves ~$5/mo + eliminates cold starts permanently',
      'This is when D3 IB Gateway on VM-2 becomes the production backend',
    ],
    prereqs: ['OCI-2 complete and shadow-tested', 'Phase C feature complete'],
    doD: ['Railway decommissioned', 'All traffic on VM-1', 'No Railway cost', 'D3 on VM-2 is production'],
    smokeChecks: [],
    files: ['backend/server.js', 'HANDOVER.md'],
    vendorDeps: [{ name: 'Railway', plan: '~$5/mo', status: 'CANCEL when OCI-10 complete' }],
  },

  {
    id: 'oci-11-performance-baseline',
    sessionNumber: 'TBD',
    name: 'OCI-11 — Performance Baseline Before Railway Termination',
    status: 'planned',
    phase: 'Phase 3 — OCI Infrastructure',
    items: [
      'Must complete BEFORE Railway is terminated — cannot confirm VM-1 handles load without testing',
      'Artillery or k6 load test: 100 concurrent users, 10 minutes sustained',
      'Verify: p95 response time < 500ms, zero 5xx errors under load',
      'MongoDB Atlas connection pool behavior under concurrent load (check pool exhaustion)',
      'OCI ARM CPU throttling: ARM VMs have burstable credits — confirm no throttling under sustained load',
      'Redis connection pool under concurrent screener requests',
      'After baseline confirmed passing: terminate Railway service',
      'Without baseline: cannot distinguish VM-1 performance issues from Railway termination risk',
    ],
    prereqs: ['OCI-10 Railway migration cutover complete', '24hr monitoring stable post-cutover'],
    doD: [
      'Load test results committed to repo (tasks/oci-11-baseline-results.md)',
      'p95 < 500ms confirmed',
      'Zero 5xx confirmed',
      'Railway service terminated',
    ],
    d3Value: 'Capital protection — D3 cannot run on an undertested VM. Baseline is the proof before IB Gateway.',
    fourProductNote: 'All 4 products depend on VM stability. Product 1 screener + Product 2 D3 + Products 3+4 portals all served from VM-1. WAF protects all.',
  },

  {
      id: 'oci-fallback-hetzner',
      sessionNumber: '02cd4d7',
      name: 'OCI Fallback — Hetzner CX22 if Oracle Blocked 60+ Days',
      status: 'planned',
      phase: 'Phase 3 — OCI Infrastructure',
      description: 'OCI Oracle ticket is the D3 blocker. If unresolved for 60 days, provision Hetzner CX22 as bridge server. Do not wait indefinitely — D3 progress cannot be blocked by Oracle bureaucracy.',
      items: [
        'TRIGGER: if OCI Oracle ticket unresolved by day 60 from application — act immediately',
        'Hetzner CX22: €3.79/mo · 2 vCPU · 4GB RAM · 80GB SSD · Amsterdam or Helsinki · persistent 24/7 · static IP',
        'IB Gateway minimum requirement: 2GB RAM for Java process — CX22 meets this comfortably',
        'Hetzner vs alternatives: DigitalOcean Droplet $12/mo (2GB), Vultr $10/mo (2GB) — Hetzner wins on cost',
        'Step 1: Sign up hetzner.com → Create CX22 server (Ubuntu 22.04 LTS) → note static IP',
        'Step 2: Install Node.js + PM2 + IB Gateway on CX22 — same as OCI-migration session',
        'Step 3: Clone repo, configure env vars, test all backend endpoints on CX22 IP',
        'Step 4: Update Railway backend URL → CX22. Post-deploy check passes → decommission Railway.',
        'Step 5: Document CX22 credentials in HANDOVER Section 52 — encrypted, not in repo',
        'Stooq 506MB data: CX22 80GB is sufficient. OCI ATP 20GB free still preferred — use Hetzner for compute only.',
        'Migration back to OCI: when Oracle ticket resolves, migrate from Hetzner to OCI. Keep Hetzner active 48hrs overlap.',
        'Cost: €3.79/mo vs OCI $0/mo. Worth it to unblock D3 by 6+ months.',
      ],
      prereqs: ['OCI ticket open 60+ days without resolution'],
      doD: [
        'CX22 running Node.js backend on port 3001 with PM2',
        'IB Gateway installed and starting on port 4001',
        'All 6 post-deploy checks pass against Hetzner backend URL',
        'Railway decommissioned',
        'sessions.js: oci-fallback-hetzner → done',
      ],
      smokeChecks: [],
      files: ['HANDOVER.md', 'backend/server.js'],
      vendorDeps: [
        { name: 'Hetzner CX22', plan: '€3.79/mo', status: 'FALLBACK — activate only if OCI blocked 60d' },
      ],
    },


  // ─── PHASE B REMAINING PLANNED ──────────────────────────────────────────

  {
      id: 'screener-enhance-1',
      sessionNumber: 'S27D-SE1',
      name: 'Screener Enhancement 1 — Algorithm Explanations + Scan Context',
      status: 'superseded',
      note: 'Superseded by EVAL-SCREENER (sessions-active.js) which covers this scope + manual filter mode + fragmentation. Do not build separately.',
      phase: 'Phase 5 — Screener Completion',
      items: [
        'Each pre-built scan shows: what algorithm it uses, what signals it looks for, which D3 strategy it maps to',
        'Scan result count badge on each button: "Top BULLISH (12)" — shows how many match before clicking',
        'Result rows: add mini sparkline (7-day price direction) — no new API calls, computed from cached data',
        'Scan result context panel: "14 of 20 tickers are BULLISH today — market in risk-on mode"',
        'Guest scan explanation: collapsed by default, expand to see criteria — increases trust + invite conversion',
        'D3 value: scan explanation click = member understanding signal — feeds strategy education loop',
      ],
      prereqs: ['S27D ✅ deployed', 'screener showing real data'],
      doD: ['Every scan has algorithm tooltip', 'Result count on scan buttons', 'Context panel visible'],
    },

  {
      id: 'screener-manual-1',
      sessionNumber: 'S27D-SE2',
      name: 'Screener Enhancement 2 — Manual Filter Scanner (Finviz-equivalent)',
      status: 'planned',
      phase: 'Phase 5 — Screener Completion',
      items: [
        'TWO screener modes: AI Scans (current) + Manual Filters (new)',
        'Manual mode — Descriptive filters: Exchange, Sector, Industry, Market Cap range, Average Volume',
        'Manual mode — Technical filters: Price Change % range, Volume vs Avg ratio, Signal (BULLISH/NEUTRAL/BEARISH), Score range (0-100)',
        'Manual mode — Fundamental filters: P/E range, EPS Growth, Dividend Yield — ONLY after FMP/Marketstack confirmed',
        'Filter combination: AND logic — all filters apply simultaneously like Finviz',
        'Save scan as preset (member only) — named, persisted in MongoDB, recalled with one click',
        'Multiple view modes: Overview (current) | Technical (score+RSI+volume) | Fundamental (P/E+EPS+yield)',
        'Guest: 3 filters max + top 10 results — member: unlimited filters + all results + CSV',
        'D3 value: saved presets = persistent strategy fingerprint per member — feeds D3 strategy assignment',
      ],
      prereqs: ['S27D-SE1 done', 'FMP Basic $19/mo confirmed OR Marketstack Basic $8.99/mo confirmed'],
      doD: ['Manual filter mode working', '3 view types switchable', 'Guest limit enforced', 'Preset save for members'],
    },

  {
      id: 'fast-news-feed',
      sessionNumber: '02cd4d7',
      name: 'Fast News Feed Evaluation — Sub-Minute for Speculation Strategy',
      status: 'planned',
      phase: 'Phase 6 — Operational Hardening',
      items: [
        'PROBLEM: AV NEWS_SENTIMENT has 15-min delay — too slow for D3 speculation strategy which needs news within 60 seconds',
        'Evaluate Benzinga Pro API: real-time news API, ~$40-150/mo depending on plan',
        'Polygon.io news: PERMANENTLY BANNED (L4) — do not evaluate',
        'Evaluate Unusual Whales news alerts: may be fast enough, already subscribed at D3a',
        'Evaluate AV premium real-time news: does Plan30 include faster news or price data only?',
        'Evaluate Finnhub news on $50/mo paid plan: confirm if real-time news included',
        'Decision: pick ONE fast news vendor, document ToS + pricing + latency in VENDOR_REGISTRY',
        'Fast news is ONLY needed for D3a+ speculation strategy — NOT for guest/member display',
        'Phase B: document decision. Do not subscribe until D3a start.',
      ],
      prereqs: ['Phase B start — before S27D screener builds'],
      doD: [
        'Fast news vendor chosen and documented',
        'VENDOR_REGISTRY updated with latency test results',
        'sessions.js: fast-news-feed → done',
      ],
      smokeChecks: [],
      files: ['src/data/sessions.js', 'HANDOVER.md'],
      vendorDeps: [
        { name: 'Benzinga Pro API', plan: '~$40-150/mo', status: 'EVALUATE — decision this session' },
      ],
    },

  {
      id: 'member-data-d3',
      sessionNumber: '02cd4d7',
      name: 'Member Data as Proprietary D3 Signal Pipeline',
      status: 'planned',
      phase: 'Phase 6 — Operational Hardening',
      description: 'UNIQUE EDGE: Member actions on MarketPulze generate proprietary crowd signal data that no vendor sells anywhere. Every watchlist add, search, calculator use, and AI analysis request = a D3 training signal. 500+ self-selected serious traders = highest quality retail signal available. Build the anonymous aggregated pipeline.',
      items: [
        'Member watchlist add/remove events → crowd accumulation/distribution detector (anonymous, aggregated)',
        'Guest ticker search frequency → pre-momentum retail interest scanner (leading indicator, 15-min rolling)',
        'Calculator usage on a ticker → retail conviction signal (multi-week holding interest)',
        'AI analysis request volume per ticker → what stocks members are seriously evaluating right now',
        'News tab click patterns by ticker → what retail is reading in real-time (sentiment proxy)',
        'Store all signals in MongoDB SignalLog with: signal_type, ticker, weight, timestamp, D3_tier',
        'Analytics endpoint: which member signals correlated with subsequent price moves? 7/14/30-day rolling',
        'D3 T3 Sentiment gate: member crowd signal = one of the 5 sentiment inputs alongside congressional + insider',
        'Rule 20 check: (1) drives upgrades when shown → YES (2) feeds D3 T3 → YES (3) proprietary → YES',
        'LEGAL: always aggregated and anonymous — never individual member tracking, never expose personal data',
      ],
      d3Value: 'Proprietary signal no vendor sells at any price. Unique competitive moat grows with membership.',
      prereqs: ['500+ active members to generate statistically meaningful signal volume'],
      doD: [
        'Member signal pipeline logging to MongoDB SignalLog with D3-compatible schema',
        'D3 T3 gate reading member crowd signal alongside congressional + insider signals',
        '/roadmap or /trading analytics showing signal accuracy vs price outcomes over 30 days',
        'sessions.js: member-data-d3 → done',
      ],
      smokeChecks: [],
      files: ['backend/routes/stocks.js', 'backend/models/SignalLog.js', 'src/data/sessions.js'],
      vendorDeps: [],
    },

  {
      id: 'waitlist-warm-email',
      sessionNumber: '02cd4d7',
      name: 'Waitlist Warm Email — Monthly Newsletter to Pending Invite Requests',
      status: 'planned',
      phase: 'Phase 6 — Operational Hardening',
      description: 'Every pending invite request is a future subscriber. Currently they request access and hear nothing. A monthly "what is new at MarketPulze" email keeps them warm so when subscriptions launch they convert at 5-10x better than cold traffic.',
      items: [
        'Monthly email to all InviteRequest records with status=pending',
        'Content: 3-4 new features shipped this month + 1 screenshot or stat + "Still building something special" tone',
        'Subject line formula: "MarketPulze update: [X] shipped this month · [N] members now active"',
        'Unsubscribe link mandatory: add unsubscribed=true flag to InviteRequest schema. Never email again if set.',
        'CAN-SPAM + GDPR: they opted in by requesting an invite. Still include one-click unsubscribe.',
        'Volume: SMTP2GO free 1000/mo easily covers this. Even 500 pending requests = 500 emails/mo.',
        'Trigger: PM2 cron job on 1st of each month OR Railway cron (if Railway still active)',
        'Analytics: track opens/clicks by adding UTM parameters to links. Feeds into conversion data.',
      ],
      prereqs: ['InviteRequest model has unsubscribed field', 'SMTP2GO working'],
      doD: [
        'Monthly cron job sending to pending invite requests',
        'Unsubscribe link working',
        'First email sent and confirmed delivered',
        'sessions.js: waitlist-warm-email → done',
      ],
      smokeChecks: [],
      files: ['backend/routes/admin.js', 'backend/models/InviteRequest.js'],
      vendorDeps: [{ name: 'SMTP2GO', plan: 'Free 1000/mo', status: 'Already active' }],
    },

  {
      id: 'referral-mechanics',
      sessionNumber: '02cd4d7',
      name: 'Referral Skip-Queue — Word-of-Mouth Without Ad Spend',
      status: 'planned',
      phase: 'Phase 6 — Operational Hardening',
      description: 'Invite-only creates natural scarcity and desire. Adding a skip-queue mechanic creates word-of-mouth: existing members can nominate 1 person for immediate invite access. The nominator is motivated (social capital) and the nominee is pre-qualified (vouched for). Zero ad spend required.',
      items: [
        'Each active member gets 1 nomination credit. Used credits cannot be reused.',
        'Nomination flow: member visits /settings → "Invite a Friend" → enters email → sends invitation',
        'Nominee receives email: "Your friend [first name only] has given you priority access to MarketPulze"',
        'Nominee clicks link → landing page → Create Account (skips the waitlist)',
        'Backend: add referredBy field to User model. Track nomination credit use.',
        'Limit: 1 nomination per member at launch. Can expand to 2-3 as product matures.',
        'Analytics: track referral conversion rate (nominations sent vs accounts created)',
        'Virality mechanic: show member their referral status "You have 1 invitation to give"',
        'NEVER make referral a requirement — members who do not refer are still valuable',
      ],
      prereqs: ['Member auth working', 'Settings page exists'],
      doD: [
        'Nomination flow working end-to-end',
        'referredBy field on User model',
        'Nomination credit tracked (cannot double-nominate)',
        'Referral email template in SMTP2GO',
        'sessions.js: referral-mechanics → done',
      ],
      smokeChecks: [],
      files: ['backend/routes/auth.js', 'backend/models/User.js', 'src/pages/SettingsPage.jsx'],
      vendorDeps: [{ name: 'SMTP2GO', plan: 'Free 1000/mo', status: 'Already active' }],
    },

  {
      id: 'office365-migration',
      sessionNumber: '02cd4d7',
      name: 'Office 365 Email Migration — support@marketpulze.com',
      status: 'planned',
      phase: 'Phase 6 — Operational Hardening',
      items: [
        '⚠️ RENEWAL REMINDER: GoDaddy subscription expires Aug/Sep 2026 — renew BEFORE expiry, independent of any session',
        'Microsoft 365 Business Basic via GoDaddy — ~$6/mo (~$72/yr)',
        'Step 1: Add Microsoft 365 Business Basic plan via GoDaddy account',
        'Step 2: Verify domain ownership (MX + TXT records via Cloudflare DNS)',
        'Step 3: Create support@marketpulze.com Outlook mailbox',
        'Step 4: Update SMTP2GO verified sender domain to match new address',
        'Step 5: Update ADMIN_EMAILS env var in Railway to include support@marketpulze.com',
        'Step 6: Test SMTP2GO digest send → confirm receipt at Outlook address',
        'Step 7: Migrate all project comms from Gmail → support@marketpulze.com',
        'Professional email separates product from personal Gmail for all communications',
      ],
      prereqs: ['S27A shipped to guests'],
      doD: [
        'support@marketpulze.com receives email in Outlook',
        'SMTP2GO sends from support@marketpulze.com successfully',
        'Railway ADMIN_EMAILS updated',
        'All project comms migrated from Gmail',
      ],
      smokeChecks: [],
      files: [],
      vendorDeps: [{ name: 'GoDaddy Outlook Email', plan: 'Microsoft 365 Business Basic ~$72/yr', status: 'PLANNED — after S27A ships' }],
    },

  {
      id: 'digest-content-spec', sessionNumber: 'S25-spec',
      name: 'Daily Digest Content Strategy',
      status: 'planned', phase: 'Phase 6 — Operational Hardening',
      items: [
        'Digest opt-in mechanics exist (S21) — content spec not defined',
        'Vague market digest = unsubscribe. Personalised watchlist digest = sticky retention.',
        'Content: (1) watchlist signal changes since last digest',
        'Content: (2) earnings tomorrow for watched tickers with AI preview link',
        'Content: (3) one AI market observation — sector rotation, unusual volume',
        'Delivery: 6am ET weekdays only. Max 5 items. Mobile-readable in 30 seconds.',
      ],
      prereqs: ['S21 digest live', 'Watchlist live'],
      doD: ['Digest sends personalised content', 'Open rate > 30% first month'],
      smokeChecks: [], files: ['backend/routes/watchlist.js'],
      vendorDeps: [{ name: 'SMTP2GO', plan: 'Free 1000/mo', status: 'active' }],
    },

  {
      id: 'sentry-monitoring', sessionNumber: '02cd4d7',
      name: 'Sentry Error Monitoring — Frontend + Backend',
      status: 'planned', phase: 'Phase 6 — Operational Hardening',
      items: [
        'Sentry.io free — 5,000 errors/mo, no credit card needed',
        'Frontend Sentry.init() — catches all React runtime errors with full stack trace',
        'Backend Sentry.init() in server.js — catches all unhandled Express errors',
        'Without Sentry: only know about errors when users email. With Sentry: alert in 60s.',
        'Add SENTRY_DSN to .env.example and Railway env vars',
      ],
      prereqs: ['S27A complete'],
      doD: ['Sentry dashboard live', 'Test error captured within 60s'],
      smokeChecks: ['grep -q "SENTRY_DSN" backend/.env.example && echo "Sentry DSN documented"'],
      files: ['src/App.jsx', 'backend/server.js', 'backend/.env.example'],
      vendorDeps: [{ name: 'Sentry.io', plan: 'Free 5k/mo', status: 'signup when ready' }],
    },

  {
      id: 'google-analytics', sessionNumber: '02cd4d7',
      name: 'Google Analytics 4 — Guest Conversion Funnel',
      status: 'planned', phase: 'Phase 6 — Operational Hardening',
      items: [
        'GA4 on guest pages only — NEVER on /admin /roadmap /trading',
        'Track: page views, Request Invite CTA clicks, invite form submissions',
        'Track: guest to stock page to gate hit to CTA click conversion funnel',
        'Without GA4 you are building blind — zero data on what converts guests',
        'Privacy Policy must be live and URL-accessible before GA4 (GDPR + AdSense)',
        'Add cookie consent banner to footer for EU visitors',
      ],
      prereqs: ['/privacy URL accessible after Task 5 fix'],
      doD: ['GA4 property receiving events', 'CTA click tracked'],
      smokeChecks: ['grep -q "gtag" public/index.html && echo "GA4 script present"'],
      files: ['public/index.html', 'src/components/Footer.jsx'], vendorDeps: [],
    },

  {
      id: 'mobile-responsive', sessionNumber: '02cd4d7',
      name: 'Mobile Responsive Audit — Before Screener Ships',
      status: 'planned', phase: 'Phase 6 — Operational Hardening',
      items: [
        'All styles inline — no CSS breakpoints defined anywhere in the product',
        'Google mobile-first ranking — broken mobile = lost SEO traffic',
        'Screener (S27D) = biggest SEO page — must work on mobile before launch',
        'Audit all guest pages at 375px (iPhone SE) and 768px (tablet)',
        'Fix: responsive grids, 44px min tap targets on CTAs, no horizontal scroll',
      ],
      prereqs: ['S27D screener spec complete'],
      doD: ['All guest pages usable at 375px', 'No horizontal scroll on mobile'],
      smokeChecks: ['grep -q "viewport" public/index.html && echo "Viewport meta present"'],
      files: ['public/index.html', 'src/pages/StockPage.jsx'], vendorDeps: [],
    },

  {
      id: 'architecture-tab',
      sessionNumber: '02cd4d7',
      name: 'Architecture Tab — Full System Diagram in /roadmap',
      status: 'planned',
      phase: 'Phase 6 — Operational Hardening',
      description: 'The /roadmap page has 8 tabs but no dedicated system architecture view. As D3 complexity grows, a single-page visual showing the complete technical architecture (frontend, backend, data vendors, D3 engine, infrastructure) is essential for development continuity and onboarding any future collaborator.',
      items: [
        'RoadmapPage already has 10 tabs ✅ — Architecture tab is the 3rd tab (ArchTab)',
      'This session: populate ArchTab with full current system data from sessions.js',
      'Ensure ArchTab renders VENDOR_REGISTRY and D3_SIGNAL_UNIVERSE from live data',
        '── WHAT THE ARCHITECTURE TAB SHOWS ─────────────────────────────────',
        'Section 1: Full stack diagram — Cloudflare Pages → Railway/OCI → MongoDB Atlas → all vendor APIs',
        'Section 2: D3 engine architecture — IB Gateway → strategy engine → veto spheres → execution → SignalLog',
        'Section 3: Data flow diagram — which vendor feeds which feature (VENDOR_REGISTRY visualised)',
        'Section 4: D3 signal chain — T1 regime → T2 commodities → T3 sentiment → T4 momentum → T5 execution',
        'Section 5: Infrastructure map — OCI/Hetzner/AWS comparison + current status',
        'Section 6: Security architecture — secret rotation schedule, encryption layers, access tiers',
        '── IMPLEMENTATION ────────────────────────────────────────────────────',
        'Read VENDOR_REGISTRY and D3_SIGNAL_UNIVERSE from sessions.js (already exported)',
        'Use inline SVG React components — consistent with inline styles rule',
        'Each section collapsible — show/hide to manage visual complexity',
        'Print/export button: window.print() on the architecture page for offline reference',
        '── LIVING DOCUMENT RULE ─────────────────────────────────────────────',
        'Every new session that adds infrastructure or a D3 component must update the architecture tab',
        'Architecture tab is the visual equivalent of HANDOVER.md — kept current every session',
      ],
      prereqs: ['RoadmapPage 8 tabs working (done)'],
      doD: [
        '9th Architecture tab added to /roadmap (Super Admin only)',
        'All 6 sections present with current accurate data',
        'VENDOR_REGISTRY and D3_SIGNAL_UNIVERSE data rendered from sessions.js (not hardcoded)',
        'sessions.js: architecture-tab → done',
      ],
      smokeChecks: [
        'grep -q "ArchTab" src/pages/RoadmapPage.jsx && echo "ArchTab component present" || echo "MISSING ArchTab"',
      ],
      files: ['src/pages/RoadmapPage.jsx', 'src/data/sessions.js'],
      vendorDeps: [],
    },

  {
      id: 'cookie-consent-banner',
      sessionNumber: '02cd4d7',
      name: 'Cookie Consent Banner — AdSense Prerequisite',
      status: 'planned',
      phase: 'Phase 6 — Operational Hardening',
      description: 'AdSense requires a cookie consent mechanism for EU/UK/EEA visitors. This is the ONLY remaining technical item blocking AdSense application. Build it Phase B early so 2-4 week approval time runs while other features are being built.',
      items: [
        'Simple banner: "We use cookies to improve your experience and show relevant ads. [Accept] [Reject]"',
        'Accept = set cookie consent=true in localStorage. Reject = set consent=false.',
        'If consent=false: do not load AdSense script. If consent=true: load AdSense.',
        'Banner shows on first visit. Dismissed after choice. Does not re-appear.',
        'GDPR requirement: clearly link to /privacy. CCPA: California residents have right to opt-out.',
        'NEVER use a dark pattern: pre-ticked "accept all", required to find "reject" — AdSense rejects sites with dark patterns',
        'Implementation: ~30 lines in App.jsx. No external library needed.',
        'Apply for AdSense IMMEDIATELY after this ships. Include privacy policy URL and traffic source.',
        'AdSense approval checklist: cookie consent ✓ · privacy policy ✓ · terms ✓ · 229 stock pages ✓ · organic traffic (need 30-60 day history)',
      ],
      prereqs: ['privacy page live (done)', 'terms page live (done)'],
      doD: [
        'Cookie banner visible on first visit to marketpulze.com',
        'AdSense script conditional on consent',
        'AdSense application submitted',
        'sessions.js: cookie-consent-banner → done',
      ],
      smokeChecks: ['grep -q "cookie" src/App.jsx && echo "Cookie consent present" || echo "MISSING cookie consent"'],
      files: ['src/App.jsx', 'src/components/CookieBanner.jsx'],
      vendorDeps: [{ name: 'Google AdSense', plan: 'Revenue share ~$0.50-2 RPM', status: 'Apply after this ships' }],
    },

  {
      id: 'congressional-data-decision', sessionNumber: '02cd4d7',
      name: 'Congressional Data Vendor Decision — Before S27D',
      status: 'planned', phase: 'Phase 6 — Operational Hardening',
      items: [
        'Unusual Whales $48/mo cancels after S27D — replacement needs congressional data',
        'Must decide vendor BEFORE S27D starts — not during coding',
        'Option A: Quiver Quantitative free tier — test ToS for public display first',
        'Option B: Build scraper from public House/Senate XML — zero licensing issue',
        'Option C: FMP /senate-disclosure — only if revenue conditions met (currently PAUSED)',
        'HANDOVER Section 31 has full analysis of all options. Decision by end of S27C.',
      ],
      prereqs: ['S27C complete'],
      doD: ['Vendor chosen + documented in VENDOR_REGISTRY', 'Tech spec written before S27D'],
      smokeChecks: [], files: ['src/data/sessions.js', 'HANDOVER.md'],
      vendorDeps: [{ name: 'Quiver Quantitative', plan: 'Free or $25/mo', status: 'evaluate ToS' }],
    },


  // ─── SECURITY + BACKUP (before D3 capital) ──────────────────────────────

  {
      id: 'mongodb-r2-backup',
      sessionNumber: '02cd4d7',
      name: 'MongoDB Automated Backup — Cloudflare R2 Before 100 Members',
      status: 'planned',
      phase: 'Phase 6 — Operational Hardening',
      items: [
        'TRIGGER: implement BEFORE 100 active members — not Phase C',
        'MongoDB Atlas M0 free tier has NO automated backup and NO restore point',
        'A corrupted database loses: all users, invite requests, watch lists, analysis history, SignalLog',
        'Solution: daily mongodump → Cloudflare R2 (free tier: 10GB storage, free egress)',
        'R2 vs S3: R2 has zero egress fees — S3 charges $0.09/GB egress. R2 wins for this use case.',
        'Setup: PM2 cron job OR Railway cron job → mongodump → pipe to Cloudflare R2 via rclone or API',
        'Retention: keep 7 daily + 4 weekly + 3 monthly. Delete older. Total ~14 snapshots.',
        'Restore test: MANDATORY — run a restore drill before declaring this done. Never trust untested backups.',
        'MongoDB Atlas M2 upgrade ($9/mo): adds continuous backup + point-in-time restore. Consider at Phase C.',
        'Alert: if backup job fails → SMTP2GO email to owner. Never silent failure.',
      ],
      prereqs: ['Cloudflare R2 bucket created', 'rclone or R2 API credentials in Railway env'],
      doD: [
        'Daily backup running and verified via restore drill',
        'Backup failure sends email alert',
        'R2 bucket has 7+ days of snapshots',
        'sessions.js: mongodb-r2-backup → done',
      ],
      smokeChecks: ['grep -q "CLOUDFLARE_R2" backend/.env.example && echo "R2 credentials documented"'],
      files: ['backend/scripts/backup.js', 'backend/.env.example'],
      vendorDeps: [{ name: 'Cloudflare R2', plan: 'Free 10GB storage', status: 'ACTIVATE before 100 members' }],
    },

  {
      id: 'secret-rotation',
      sessionNumber: '02cd4d7',
      name: 'Secret Rotation + Super Admin Vault — Security Hardening',
      status: 'planned',
      phase: 'Phase 6 — Operational Hardening',
      description: 'Operational security. IBKR password, API keys, JWT secret all need rotation procedures. Super Admin needs encrypted vault view. Currently changing any secret requires manual Railway restart. This must be documented before D3 holds real capital.',
      items: [
        '── SECRET ROTATION PROCEDURES (document + test each) ────────────────',
        'JWT_SECRET rotation: generate new secret → update Railway env → restart → all sessions invalidated (expected) → users re-login',
        'IBKR password rotation: change in IBKR Client Portal → update OCI/Hetzner env → restart IB Gateway PM2 process → verify reconnect',
        'Anthropic API key rotation: generate new key in Anthropic Console → update Railway env → verify MD7 brief still works',
        'SMTP2GO credentials: generate new API key in SMTP2GO dashboard → update Railway env → send test email',
        'SUPER_ADMIN_EMAILS: this is configuration not a secret — document change procedure for team handoff',
        'MongoDB Atlas connection string: rotate in Atlas → update Railway env → test all DB operations',
        'Rotation frequency: JWT_SECRET every 6 months. API keys: rotate on any suspected breach. IBKR: follow IBKR policy.',
        '── SUPER ADMIN VAULT VIEW (/trading/vault) ──────────────────────────',
        'Super Admin only — completely hidden from all other users and admins',
        '/trading/vault — shows: last rotation date per secret, days since rotation, rotation status (green/amber/red)',
        'NEVER shows secret values — shows metadata only (last 4 chars of key, rotation date, source)',
        'Rotation alerts: if JWT > 180 days old → amber warning. If IBKR password > 90 days → amber.',
        'One-click rotation trigger for API keys that support programmatic rotation (Anthropic, AV)',
        'Audit log: every secret rotation logged with timestamp, who triggered it (always owner — single account)',
        '── ENCRYPTION AT REST ────────────────────────────────────────────────',
        'MongoDB Atlas M0: encryption at rest enabled by default (AES-256). Confirmed.',
        'Railway environment variables: encrypted at rest by Railway. Do not store in code or logs.',
        'OCI/Hetzner: use Linux keyring or HashiCorp Vault lite (free) for IB Gateway credentials',
        'Never log API keys, passwords, or JWT tokens in any backend console.log or error handler',
        '── INCIDENT RESPONSE ────────────────────────────────────────────────',
        'If API key compromised: (1) revoke immediately in vendor console (2) update env (3) restart service (4) check access logs',
        'If IBKR password compromised: (1) change in IBKR immediately (2) disable API access temporarily (3) update OCI env (4) review recent trades',
        'If MongoDB connection string leaked: (1) rotate Atlas credentials (2) check Atlas access logs for unauthorized queries',
      ],
      prereqs: ['Phase B complete — before D3 handles real capital'],
      doD: [
        '/trading/vault page live (Super Admin only) — shows rotation metadata, no values',
        'All rotation procedures documented in HANDOVER Section 54',
        'Rotation tested for: JWT, Anthropic API key, SMTP2GO',
        'Zero API keys or secrets in any git commit (check with git-secrets or similar)',
        'sessions.js: secret-rotation → done',
      ],
      smokeChecks: [
        'grep -qrE "api_key|apiKey|secret|password" backend/routes/ --include="*.js" | grep -v ".env" | wc -l | xargs -I{} test {} -eq 0 && echo "No hardcoded secrets in routes" || echo "CHECK FOR HARDCODED SECRETS"',
      ],
      files: ['src/pages/RoadmapPage.jsx', 'HANDOVER.md', 'backend/routes/trading.js'],
      vendorDeps: [],
    },


  // ─── D3 STRATEGY SPECIFICATION ──────────────────────────────────────────

  {
    id: 'scenario-j-earnings-revision',
    sessionNumber: 'TBD',
    name: 'Scenario J — Earnings Revision Momentum (D3 Strategy)',
    status: 'planned',
    phase: 'Phase 8 — D3 Foundation',
    items: [
      'Academic basis: Stickel 1991, Womack 1996 — analyst herding drives 60-90 day continuation',
      'Mechanism: first analyst upgrade triggers institutional buying cascade (underreaction to revisions)',
      'Confidence: HIGH — free Finnhub data, EOD entry, fits D3a 30-day shadow scope',
      'Entry: EPS estimate revised up 5%+ by 2+ analysts within 60 days of last earnings report',
      'Price must be below post-earnings high (drift not complete)',
      'VIX < 25, Quality composite score > 60th percentile',
      'No earnings announcement within 10 days (avoids IV noise)',
      'Hold: 60-90 days. Stop: -5% from entry. EOD entry only.',
      'Data source: Finnhub /stock/price-target + /stock/earnings (free tier — available now)',
      'D3a eligible: YES — fits shadow mode scope, free data, no intraday execution required',
    ],
    prereqs: ['OCI-8 shadow mode live', 'Finnhub analyst endpoints confirmed'],
    d3Value: 'Adds end-of-day medium-term strategy requiring no real-time data. Highest confidence new scenario.',
  },

  {
    id: 'scenario-k-volatility-regime',
    sessionNumber: 'TBD',
    name: 'Scenario K — Volatility Regime Rotation (D3 Portfolio Strategy)',
    status: 'planned',
    phase: 'Phase 8 — D3 Foundation',
    items: [
      'Academic basis: Baker, Bradley, Wurgler 2011 + Ang et al. 2006 — 40+ year evidence base',
      'Confidence: FULL — most replicated anomaly in empirical finance',
      'Mechanism: institutional mandate constraints force high-beta holding → overpayment for volatility',
      'NOT a trade signal — a PORTFOLIO TILT signal affecting all position sizes',
      'Risk-OFF trigger: VIX moves from < 20 to > 25 AND HYG/LQD credit spreads widening simultaneously',
        'Apply 0.5x multiplier to ALL new position sizes',
        'Prefer new entries in low-beta (beta < 0.8) quality names only',
      'Risk-ON confirmation: VIX moves from > 30 back below 25',
        'Restore full fractional Kelly sizing',
        'Higher-beta momentum names eligible again',
      'Data source: VIX from Finnhub, HYG/LQD from MarketDashboard (already in stack)',
      'D3a eligible: YES — no new data sources required',
    ],
    prereqs: ['OCI-7 monitoring live (VIX alert integration)'],
    d3Value: 'Reduces portfolio drawdown during worst market regimes. Adds 2-3% annually to risk-adjusted returns.',
  },

  {
    id: 'scenario-l-congressional-insider',
    sessionNumber: 'TBD',
    name: 'Scenario L — Congressional + Insider Convergence (D3 Strategy)',
    status: 'planned',
    phase: 'Phase 8 — D3 Foundation',
    items: [
      'Academic basis: Ziobrowski et al. 2011 — US Senators outperformed market 12% annually',
      'Confidence: REASONABLE — evidence strong, implementation needs OCI-5 + Quiver Quantitative',
      'Mechanism: committee information advantage + corporate insider conviction converging simultaneously',
      'Entry: Congressional purchase (House Clerk XML or Quiver) + Form 4 cluster (2+ insiders $25k+)',
        'Same ticker, same 30-day window',
        'Quality composite score > 50th percentile',
        'VIX < 25',
      'Data: OCI-5 EDGAR Form 4 pipeline + Quiver Quantitative $10/mo (Phase C)',
      'D3a eligible: NO — requires OCI-5. D3b eligible when OCI-5 live.',
      'Distinguish: congressional BUYING signal (legal) vs material non-public info (illegal)',
      'All signals from public disclosure — Congressional STOCK Act + SEC Form 4 = fully public',
    ],
    prereqs: ['OCI-5 EDGAR Form 4 pipeline live', 'Quiver Quantitative subscribed ($10/mo)'],
    d3Value: 'Dual information advantage convergence — highest-quality fundamental signal in D3 universe.',
  },

  {
    id: 'd3-strategy-specification',
    sessionNumber: 'TBD',
    name: 'D3 Strategy Specification — Complete Pseudocode Before OCI-8',
    status: 'planned',
    phase: 'Phase 8 — D3 Foundation',
    items: [
      'Must complete BEFORE OCI-8 shadow mode starts — without this, accuracy measurement is meaningless',
      'Output: single document with exact entry conditions per scenario in pseudocode',
      'Output: exact exit conditions per scenario (signal reversal, trailing stop, time, concentration, partial)',
      'Output: portfolio constraint framework — max positions, sector caps, cash reserve, Kelly formula',
      'Output: position sizing formula per scenario with D3a defaults (2% until win rates established)',
      'Output: regime gate conditions — VIX+HYG/LQD hard stop parameters',
      'Output: Tier 3 signal integration schema — crowd_support_zones, demand_flag, etc.',
      'Output: L6 counterfactual analysis structure + proposed_filters review protocol',
      'D3a scope confirmed: E+G convergence + H breakout + 52W High breakout ONLY',
        'Attribution requires isolation — cannot evaluate signal quality with all 9 running simultaneously',
      'D3b additions: Scenario B (momentum intraday — needs IBKR L2) + Scenario G standalone',
      'D3c additions: validated patterns only — never add Scenario A pump LONG or F Mode 3 short with real capital',
    ],
    prereqs: ['OCI-7 monitoring + safety complete', 'ManualTradeJournal loaded with 1+ year data'],
    doD: [
      'strategy-spec.md document committed to repo',
      'All entry conditions in pseudocode (machine-verifiable)',
      'All exit conditions specified with exact thresholds',
      'Portfolio framework numerically specified',
      'L6 review protocol documented',
    ],
    d3Value: 'Without this session, OCI-8 shadow mode starts underdetermined. The document IS the test spec.',
  },


  // ─── PORTALS + AUTH + DR ────────────────────────────────────────────────

  {
    id: 'options-flow-display',
    sessionNumber: 'OPTIONS-FLOW-DISPLAY',
    name: 'Options Flow Display — Unusual Activity + IV Percentile + Put/Call Ratio (Subscriber Feature)',
    status: 'planned',
    phase: 'Phase 7 — Revenue Gate',
    items: [
      '── WHY PHASE 7 (REVENUE GATE) ─────────────────────────────────',
      'Options flow data costs $50-200/mo. It justifies a Pro/Premium subscription tier.',
      'Build after first subscribers — not before. Paying for data with no revenue = waste.',
      'Unusual Whales $50/mo is already planned for pump-detection (Phase 10).',
      'If subscribed at Phase 7, data accumulates months before Phase 10 needs it.',
      '── SCREENER: UNUSUAL OPTIONS ACTIVITY TAB ─────────────────────',
      'Top tickers with options volume > 200% of 30-day average',
      'Columns: ticker · call/put ratio · unusual volume % · dominant expiry · premium spent · signal',
      'Sweep orders vs limit: sweeps = institutional urgency = higher conviction',
      'Sentiment: BULLISH (calls dominating sweeps) / BEARISH (puts dominating sweeps)',
      '── STOCK PAGE: OPTIONS SECTION ─────────────────────────────────',
      'IV current + IV percentile (where IV sits vs 52-week range)',
      'IV percentile >80% = earnings/catalyst expected (do not sell options)',
      'IV percentile <20% = historically cheap options (better buying opportunity)',
      'Put/call ratio: current vs 30-day average · divergence from price direction',
      'Open interest by strike: visualise where institutional bets are concentrated',
      'IV crush warning: premium collapses after earnings print',
      'Legal label: "Options data — informational only. Options involve risk. Not investment advice."',
      '── POST-MARKET EARNINGS INTEGRATION ────────────────────────────',
      'Post-market earnings announcement → IV crush next morning surfaced to member',
      '  "NVDA earnings beat AH +8% — IV crush expected at open. Options premium will drop."',
      '  Links from EXTENDED-HOURS-SCANNER post-market tab → options section',
      '── D3 VALUE ────────────────────────────────────────────────────',
      'Large call sweep + congressional buy = Scenario B elevated conviction',
      'Large put sweep + insider sale = Scenario F Mode 3 short confirmer',
      'IV crush post-earnings = Scenario E PEAD entry timing (enter after IV normalises)',
      'IV percentile feeds D3 volatility regime detection (Scenario K)',
      '── FOUR-PRODUCT VALUE ──────────────────────────────────────────',
      'Product 1: subscriber differentiator — replaces Market Chameleon + Unusual Whales options features',
      'Product 2 (D3): options sweep confirms or contradicts other signal tiers',
      'Product 3 (Inner Circle): IV percentile context in sector rotation brief',
      'Product 4 (Commercial): options sentiment = institutional positioning context in signal feed',
    ],
    prereqs: [
      'First paying subscribers exist (Phase 7 trigger)',
      'ADMIN-PORTAL live (subscriber tier management)',
      'Unusual Whales API subscribed ($50/mo)',
    ],
    doD: [
      'Unusual options activity tab on screener: top 20 by volume anomaly',
      'Sweep identification active (institutional urgency)',
      'Stock page: IV + IV percentile + P/C ratio + open interest by strike',
      'IV crush warning on earnings integration',
      'Legal labels on all options displays',
      'Feature gated to Pro/Premium subscribers only',
    ],
    smokeChecks: [
      'grep -q "optionsFlow" src/pages/ScreenerPage.jsx',
      'grep -q "ivPercentile" src/pages/StockPage.jsx',
    ],
    files: [
      'src/pages/ScreenerPage.jsx',
      'src/pages/StockPage.jsx',
      'backend/routes/screener.js',
      'backend/routes/stocks-seo.js',
    ],
    vendorDeps: [
      { name: 'Unusual Whales', plan: '$50/mo', endpoint: 'Options flow + unusual activity', status: 'Subscribe Phase 7 — also needed Phase 10 pump-detection' },
    ],
  },

  {
    id: 'ADMIN-PORTAL',
    sessionNumber: 'ADMIN-PORTAL',
    name: 'Admin Portal — admin.marketpulze.com + Stripe Billing + Member Management',
    status: 'planned',
    phase: 'Phase 7 — Revenue Gate',
    items: [
      'TRIGGER: first paying subscriber — do not build before subscriptions launch',
      '',
      '── WHY SEPARATE FROM /admin ──────────────────────────────────────',
      'Phase B: /admin in main app is sufficient (invite-only, no billing, small team)',
      'Phase C trigger: billing disputes need audit trail separate from app',
      'Stripe webhook handling needs dedicated surface',
      'Billing admin actions need independent log (legal + compliance)',
      'Stricter session (15 min) + MFA for billing access',
      '',
      '── MOVE FROM /admin (existing) ───────────────────────────────────',
      'User management: invite approval, role assignment, tester management',
      'Rate limit config: guest_screener_limit, guest_stockpage_limit etc',
      'Audit log: all admin actions with timestamp + actor',
      'System config: Admin Settings tab (all existing getConfig keys)',
      '',
      '── NEW AT PHASE C ────────────────────────────────────────────────',
      'Stripe integration: subscription creation, renewal, cancellation',
      'Billing dashboard: MRR, churn, subscriber count, revenue by tier',
      'Member tier management: Free → Pro → Team upgrades/downgrades',
      'Invoice history: per-member invoice list with download',
      'Failed payment handling: retry logic + member notification flow',
      'Subscription renewal management: upcoming renewals, grace periods',
      'Member activity tracing: full event log per member (screener, chart, watchlist)',
      'Bulk actions: suspend, upgrade, downgrade, export CSV of members',
      '',
      '── AUTH + SECURITY ──────────────────────────────────────────────',
      'Separate subdomain: admin.marketpulze.com',
      'Auth: MongoDB JWT + requireAdmin + TOTP MFA (stricter than main app)',
      'Session: 15 minutes (vs 24hr on main site)',
      'All admin actions logged to independent audit collection',
      'Stripe webhook endpoint: /api/webhooks/stripe (separate from admin routes)',
      '',
      '── DUAL ACCESS TIER DESIGN (flagged DOC-SYNC May 2026) ──────────',
      'Admin panel already has two access tiers built: regular admin + Super Admin.',
      'Current: SUPER badge appears for Super Admin · same URL · role-based UI differences.',
      'Decision at session open: does regular admin get same subdomain (admin.marketpulze.com)',
      '  with role-based tab visibility? Or separate URL for each tier?',
      'Super Admin trading suite (trading.marketpulze.com) is ALWAYS separate — never shares.',
      'Regular admin scope: member management · requests · testers · rate config · billing',
      'Super Admin scope: everything + system config · pipeline status · D3 signal monitoring · kill switch',
      'Principle: regular admin never sees D3 engine status or trading controls.',
      'Super Admin never manages members via trading portal — use admin portal for that.',
    ],
    prereqs: [
      'S27J deployed (saved scans + alerts — member engagement features)',
      'OCI-6 screener UI live (all signal columns visible to subscribers)',
      'First paying subscriber exists',
      'Stripe account created + webhook configured',
    ],
    doD: [
      'admin.marketpulze.com loads for requireAdmin users',
      'Stripe subscription creation + renewal working end to end',
      'Member activity tracing dashboard shows per-member events',
      'All existing /admin functionality migrated to new subdomain',
      'TOTP MFA required for admin login',
      'Audit log captures all admin actions independently',
      'CI=true npm run build clean',
    ],
    smokeChecks: [
      'test -f src/pages/AdminPortalPage.jsx',
      'grep -q "stripe" backend/routes/billing.js',
      'grep -q "adminAuditLog" backend/models/AdminAuditLog.js',
      'grep -q "admin.marketpulze.com" public/_redirects',
    ],
    files: [
      'src/pages/AdminPortalPage.jsx',
      'backend/routes/billing.js',
      'backend/routes/admin-portal.js',
      'backend/models/AdminAuditLog.js',
      'backend/models/Subscription.js',
    ],
    vendorDeps: [
      { name: 'Stripe', plan: 'Pay per transaction (~2.9% + $0.30)', status: 'Activate at Phase C' },
    ],
    d3Signal: 'member_tier_change + subscription_event → L2 willingness_to_pay signal',
  },

  {
    id: 'D3-AUTH',
    sessionNumber: 'D3-AUTH',
    name: 'D3 Agent Authentication + Signal Architecture + Inner Circle Access',
    status: 'planned',
    phase: 'Phase 8 — D3 Foundation',
    items: [
      '── ORIGINAL SCOPE ──────────────────────────────────────────────',
      'D3 agent service account: API key auth — non-expiring, no browser session dependency',
      'Separate from member JWT auth — D3 engine authenticates as service, not user',
      'Session expiry SMTP2GO alert: email when member forced-logout occurs',
      'Signal gap monitoring: SMTP2GO alert if no signals >5min during market hours',
      'D3StatusPanel extension: "Last signal: X min ago" live indicator on dashboard',
      '',
      '── SIGNAL ARCHITECTURE (added April 2026) ───────────────────────',
      'signalWriter service: Redis primary (1ms) + ATP async archive (MongoDB API)',
      'Replace direct MongoDB D3 signal writes: ScreenerSignalLog, DrawingLog, ChartInteractionLog, WatchlistLog',
      'Redis AOF persistence enabled on VM-1 — survives restart, zero signal loss',
      'ATP retry queue: failed writes go to signals:retry, background worker retries every 60s',
      'Result: MongoDB Atlas has ZERO D3 dependency — members and D3 fully segregated',
      '',
      '── INNER CIRCLE ACCESS (added April 2026) ────────────────────────',
      'Google OAuth: passport-google-oauth20 → MongoDB (NOT OCI External User domain)',
      'TOTP MFA: speakeasy library — mandatory for trading.marketpulze.com',
      'User model: isD3Viewer, d3ViewerApprovedAt, d3RiskAcceptedAt, d3ViewerScope fields',
      'Risk acceptance consent modal: scroll-to-bottom + typed confirmation + timestamp stored',
      'trading.marketpulze.com: Cloudflare DNS route + 30-min session timeout',
      'Inner circle sees: performance, signal status, D3 active/inactive ONLY',
      'Inner circle cannot: positions, strategy params, execution controls, LLM reasoning',
      '',
      '── DR READINESS ──────────────────────────────────────────────────',
      'Out-of-band emergency stop procedure: documented in TRADING.md',
      'IBKR emergency close steps: accessible without OCI being operational',
      'D3-DR-1 through D3-DR-5 rules enforced in D3 engine code',
    ],
    prereqs: [
      'OCI-2 complete (VM-1 + Redis provisioned)',
      'OCI-3 complete (VM-2 + IB Gateway)',
      'SMTP2GO confirmed working',
      'ATP MongoDB API connection verified',
    ],
    doD: [
      'D3 agent authenticates with API key (no expiry)',
      'signalWriter dual-writes to Redis + ATP for all D3 signals',
      'MongoDB Atlas has zero D3 signal reads',
      'Google OAuth works for inner circle onboarding',
      'TOTP MFA gates trading.marketpulze.com',
      'Risk acceptance flow stores d3RiskAcceptedAt',
      'SMTP2GO alert fires within 5min of signal gap',
      'IBKR emergency procedure documented',
    ],
    smokeChecks: [
      'test -f backend/services/signalWriter.js',
      'grep -q "isD3Viewer" backend/models/User.js',
      'grep -q "speakeasy" package.json',
      'grep -q "passport-google-oauth20" package.json',
    ],
    files: [
      'backend/services/signalWriter.js',
      'backend/routes/auth.js',
      'backend/middleware/auth.js',
      'backend/models/User.js',
      'backend/services/monitoring.js',
    ],
    d3Signal: 'Signal segregation: MongoDB members, Redis+ATP D3. Inner circle read-only conviction signals.',
  },

  {
    id: 'D3-PORTAL',
    sessionNumber: 'D3-PORTAL',
    name: 'D3 Control Panel — trading.marketpulze.com',
    status: 'planned',
    phase: 'Phase 8 — D3 Foundation',
    items: [
      'Full frontend for trading.marketpulze.com — D3 control and monitoring dashboard',
      '',
      '── OWNER VIEW (isSuperAdmin) ──────────────────────────────────────',
      'Live positions + P&L (D3c onwards)',
      'D3 signal feed — all 7 layers (L1-L5 outputs, L6 weight updates, L7 portfolio state)',
      'Strategy parameters — read + write with TOTP re-confirm on every change',
      'Emergency stop button — TOTP re-confirm + 5-second countdown + irreversible',
      'Manual override — enter/exit position outside D3 conviction',
      'Performance vs manual trading comparison (/trading/performance)',
      'LLM reasoning log — L3/L4/L5 decision chains',
      'Full IBKR account summary',
      'DR status panel: Redis health, ATP sync lag, VM-2 heartbeat',
      '',
      '── INNER CIRCLE VIEW (isD3Viewer — scoped read-only) ─────────────',
      'Performance summary: returns, Sharpe ratio, max drawdown, win rate',
      'Signal status: D3 active/shadow/paper/offline indicator',
      'Risk disclaimer: displayed on every page, cannot be dismissed',
      'No positions (privacy + legal)',
      'No strategy parameters (IP protection)',
      'No execution controls',
      'No LLM reasoning logs',
      '',
      '── SHARED FEATURES ────────────────────────────────────────────────',
      'Signal gap alert banner: "D3 signal feed interrupted X min ago"',
      '30-minute session timeout with countdown warning',
      'Mobile responsive — check positions and status from any device',
      'TOTP MFA gate — every login requires 6-digit code',
      'Session invalidated on any security event',
    ],
    prereqs: [
      'D3-AUTH complete (TOTP + Google OAuth + signalWriter)',
      'OCI-8 shadow mode live (D3 processing real signals)',
    ],
    doD: [
      'trading.marketpulze.com loads for isSuperAdmin + isD3Viewer',
      'Owner sees all 7 D3 layers + emergency stop',
      'Inner circle sees performance only (scope enforced server-side)',
      'TOTP MFA required on every login',
      'Signal gap banner appears within 5min of gap',
      'Mobile responsive on all viewports',
      'DR status panel shows Redis/ATP/VM-2 health',
      '30-min session timeout enforced',
    ],
    smokeChecks: [
      'test -f src/pages/TradingPortalPage.jsx',
      'grep -q "isD3Viewer" src/pages/TradingPortalPage.jsx',
      'grep -q "emergencyStop" src/pages/TradingPortalPage.jsx',
    ],
    files: [
      'src/pages/TradingPortalPage.jsx',
      'backend/routes/trading-portal.js',
      'backend/middleware/d3ViewerAuth.js',
    ],
    d3Signal: 'D3 visibility layer — inner circle conviction signals through performance transparency.',
  },

  {
    id: 'OCI-DR',
    sessionNumber: 'OCI-DR',
    name: 'OCI Disaster Recovery — Backup Automation + Frankfurt Standby',
    status: 'planned',
    phase: 'Phase 11 — D3 Capital Deploy',
    items: [
      'TRIGGER: Must complete before D3c (live trades with real capital)',
      '',
      '── BACKUP AUTOMATION ──────────────────────────────────────────────',
      'Redis AOF → OCI Object Storage: every 15 min RDB snapshot, 7 daily + 4 weekly',
      'ATP weekly export: Oracle Data Pump → Object Storage every Sunday 2am ET',
      'VM-1 custom image: weekly snapshot → Object Storage (3 retained)',
      'VM-2 custom image: weekly snapshot + after any config change (3 retained)',
      'Alert: SMTP2GO if any backup job fails',
      '',
      '── CLOUDFLARE FAILOVER AUTOMATION ────────────────────────────────',
      'Cloudflare Health Check: monitor VM-1 (marketpulze.com origin)',
      'Health check fails → automatic DNS failover to Railway origin',
      'RTO for app traffic: < 2 minutes (automated)',
      'Railway stays warm until OCI-11 termination',
      '',
      '── FRANKFURT DR REGION ────────────────────────────────────────────',
      'OCI Frankfurt (eu-frankfurt-1): standby region',
      'VM images replicated to Frankfurt Object Storage',
      'ATP cross-region backup enabled (requires paid ATP — OCI-4 prerequisite)',
      'Redis snapshot replicated to Frankfurt',
      '$0 cost when standby, hourly rate only if activated',
      '',
      '── D3 DR RULES IMPLEMENTATION ──────────────────────────────────────',
      'D3-DR-1: stop-loss set at every position open (IBKR-side, independent of D3)',
      'D3-DR-2: signal gap > 5min → D3 HOLD state + SMTP2GO alert',
      'D3-DR-3: OCI down → no new trades (by design)',
      'D3-DR-4: out-of-band emergency stop via IBKR mobile (documented)',
      'D3-DR-5: DR event recovery validation checklist before D3 resumes',
      '',
      '── DR RUNBOOK ────────────────────────────────────────────────────',
      'Written Tier 1/2/3 runbook with step-by-step procedures',
      'RTO/RPO validation test — simulate Tier 2 failure, measure actual recovery time',
      'Emergency contacts doc: OCI support, IBKR support, MongoDB Atlas',
      'TRADING.md: IBKR emergency close procedure (accessible without OCI)',
    ],
    prereqs: [
      'OCI-7 complete (monitoring + alerts)',
      'OCI-9 complete (D3a 30-day shadow run — validated before live capital)',
      'Paid ATP activated (required for cross-region backup)',
    ],
    doD: [
      'Redis + ATP + VM backups running automated, verified via restore test',
      'Cloudflare → Railway failover tested and confirmed < 2 min RTO',
      'Frankfurt DR: VM images replicated, ATP cross-region enabled',
      'DR runbook written + end-to-end test documented',
      'TRADING.md IBKR emergency close procedure accessible without OCI',
      'D3-DR-1 through D3-DR-5 all implemented and verified',
    ],
    smokeChecks: [
      'test -f scripts/backup-redis.sh',
      'test -f scripts/backup-atp.sh',
      'grep -q "DR_EVENT" backend/models/D3EmergencyLog.js',
    ],
    files: [
      'scripts/backup-redis.sh',
      'scripts/backup-atp.sh',
      'scripts/vm-snapshot.sh',
      'docs/DR_RUNBOOK.md',
      'docs/TRADING.md',
      'backend/models/D3EmergencyLog.js',
    ],
    d3Signal: 'Capital protection: D3 never loses signal data, existing positions always stop-loss protected.',
  },




  {
    id: 'd3-private-repo',
    sessionNumber: 'D3-PRIVATE-REPO',
    name: 'D3-PRIVATE-REPO: Separate Private GitHub Repo for D3 Execution Engine',
    status: 'planned',
    phase: 'Phase 8 — D3 Foundation',
    items: [
      '── WHY SEPARATE REPO ────────────────────────────────────────────',
      'D3 execution code must never be in the main marketpulze repo.',
      'Main repo deploys to Cloudflare Pages — public. Never D3 code.',
      'Private repo: no public visibility · no Cloudflare · no Railway.',
      'Deployed ONLY to VM-2 via Bastion SSH. Not via GitHub Actions public flow.',
      '── WHAT GOES IN THE PRIVATE REPO ───────────────────────────────',
      'backend/engine/d3Engine.js         ← L1-L7 orchestration',
      'backend/engine/l1Scout.js          ← OCI GenAI Llama 4 Scout calls',
      'backend/engine/l2Flash.js          ← OCI GenAI Gemini 2.5 Flash calls',
      'backend/engine/l3Maverick.js       ← OCI GenAI Llama 4 Maverick calls',
      'backend/engine/l4Validation.js     ← OCI GenAI gpt-oss-120b calls',
      'backend/engine/l5Gate.js           ← Anthropic Claude Sonnet capital gate',
      'backend/engine/l6Learning.js       ← weekly self-improvement (DuckDB + proposal)',
      'backend/engine/l7Summary.js        ← post-trade summary',
      'backend/broker/brokerAdapter.js    ← routes to correct broker adapter',
      'backend/broker/ibkrAdapter.js      ← IB Gateway calls (VM-2 only)',
      'backend/broker/ondoAdapter.js      ← future ONDO adapter stub',
      'backend/engine/tradeLog.js         ← write every trade to ATP-2 trade_log',
      '── WHAT STAYS IN MAIN REPO ──────────────────────────────────────',
      'signalWriter.js — shared signal infrastructure (public + D3)',
      'All frontend code — all member-facing features',
      'pipeline jobs — pipeline code is not engine code',
      '── DEPLOY PATTERN ───────────────────────────────────────────────',
      'Deploy key: mp-vm2-d3 (separate from mp-vm-pipeline key)',
      'Deploy method: manual git pull via Bastion SSH — never automated push to VM-2',
      'Reason: D3 code changes must be owner-reviewed before deploy. No CI auto-deploy.',
      '── BROKER ADAPTER RULE (R33 — enforced here) ────────────────────',
      'D3 engine NEVER calls IBKR or any broker directly.',
      'Always: brokerAdapter.placeOrder(order)',
      'IbkrAdapter handles IB Gateway syntax. OndoAdapter handles ONDO syntax.',
      'Adding new broker = one new adapter file. Zero engine changes.',
    ],
    prereqs: [
      'OCI-10 complete ✅ (D3a shadow run — 30 days validated)',
      'Owner satisfied with shadow mode accuracy',
    ],
    doD: [
      'Private GitHub repo created (marketpulze-d3 or similar) ✅',
      'D3 engine skeleton committed to private repo ✅',
      'brokerAdapter.js pattern implemented ✅',
      'ibkrAdapter.js connects to IB Gateway on VM-2 ✅',
      'tradeLog.js writes to ATP-2 trade_log on every trade ✅',
      'Deploy key mp-vm2-d3 added to private repo ✅',
      'No D3 engine code in main marketpulze repo ✅',
    ],
    smokeChecks: [],
    files: [],
    vendorDeps: [],
  },

  {
    id: 'd3-owner-portal',
    sessionNumber: 'D3-OWNER-PORTAL',
    name: 'D3-OWNER-PORTAL: trading.marketpulze.com — Owner Dashboard',
    status: 'planned',
    phase: 'Phase 8 — D3 Foundation',
    items: [
      '── WHAT THIS IS ─────────────────────────────────────────────────',
      'Full trading control panel for owner only at trading.marketpulze.com.',
      'Private subnet. No public IP. TOTP + JWT. No CDN caching.',
      '── OWNER VIEW (isSuperAdmin) ────────────────────────────────────',
      'Live positions + P&L (D3c onwards)',
      'Full signal stack: L1-L5 outputs · L6 proposed weight changes · L7 portfolio state',
      'L3 full reasoning text for every open position (why D3 entered, what it expects)',
      'L6 weight change proposals — owner approves or rejects each one individually',
      'Kill switch: halt all trading + close all positions',
      '  TOTP re-confirm required · 5-second countdown · irreversible until owner re-enables',
      'Manual override: enter or exit position outside D3 conviction',
      'Broker connection status per adapter (IBKR live · ONDO · others)',
      'Performance vs manual trading comparison',
      'DR status panel: Redis health · ATP-2 sync lag · VM-2 heartbeat',
      '30-minute session timeout with countdown warning',
      'TOTP MFA required on every login (no remembered devices)',
      '── INNER CIRCLE VIEW (isD3Viewer — scoped read-only) ────────────',
      'Performance summary: returns · Sharpe ratio · max drawdown · win rate (aggregate only)',
      'Rotation direction only: TOWARD_SAFETY · TOWARD_RISK · NEUTRAL (no conviction score)',
      'Sector alert notifications (delayed 15-30 minutes vs owner)',
      'D3 status indicator: active / shadow / paper / offline',
      'Risk disclaimer on every page — cannot be dismissed',
      'NEVER shows: positions · P&L details · L6 weights · L3 reasoning · execution controls',
      '  which specific stocks D3 holds · position sizes · conviction formula',
      '── PROTECTION PRINCIPLE ─────────────────────────────────────────',
      'Anyone with the full engine could replicate D3.',
      'Anyone with a 15-minute delayed directional signal feed cannot.',
      'The truncation IS the protection. Server-side scope enforcement.',
      '── TECH ─────────────────────────────────────────────────────────',
      'Separate Cloudflare Pages project — trading.marketpulze.com',
      'Private DNS — not publicly crawlable',
      'No shared React bundle with main site',
    ],
    prereqs: [
      'D3-AUTH complete (TOTP + signalWriter) ✅',
      'OCI-8 complete (VM-2 + D3 shadow mode live) ✅',
    ],
    doD: [
      'trading.marketpulze.com loads for isSuperAdmin + isD3Viewer ✅',
      'Owner sees full signal stack + kill switch + L6 proposals ✅',
      'Inner circle sees performance summary only — scope server-side enforced ✅',
      'TOTP required every login ✅',
      'L6 approval flow: owner can approve or reject each proposed weight change ✅',
      'Kill switch tested: halts D3 + closes positions + confirms via TOTP ✅',
      '30-min session timeout enforced ✅',
      'DR status panel shows Redis/ATP-2/VM-2 health ✅',
    ],
    smokeChecks: [
      'test -f src/pages/TradingPortalPage.jsx',
      'grep -q "isD3Viewer" src/pages/TradingPortalPage.jsx',
      'grep -q "emergencyStop" src/pages/TradingPortalPage.jsx',
      'grep -q "l6Approval" src/pages/TradingPortalPage.jsx',
    ],
    files: [
      'src/pages/TradingPortalPage.jsx',
      'backend/routes/trading-portal.js',
      'backend/middleware/d3ViewerAuth.js',
    ],
    d3Signal: 'D3 control layer — owner capital protection + inner circle directional awareness.',
  },

  {
    id: 'd3-inner-circle',
    sessionNumber: 'D3-INNER-CIRCLE',
    name: 'D3-INNER-CIRCLE: Inner Circle Portal Launch',
    status: 'planned',
    phase: 'Phase 12 — Products 3 and 4',
    items: [
      '── TRIGGER CONDITIONS (all must be true) ────────────────────────',
      'D3 live trading 12+ months with positive Sharpe ratio',
      'L6 has completed 12+ weekly improvement cycles',
      'Owner satisfied with signal quality and accuracy',
      'Legal review complete for signal sharing with invited individuals',
      '── WHAT INNER CIRCLE MEMBERS GET ───────────────────────────────',
      'Invitation only — no public registration ever',
      'trading.marketpulze.com with isD3Viewer scope (same portal, scoped view)',
      'Rotation direction: TOWARD_SAFETY · TOWARD_RISK · NEUTRAL',
      'Sector alerts — which sectors flagged, not conviction or reasoning',
      'Tier 1 signal notifications — delayed 15-30 minutes vs owner',
      'Aggregate performance: returns · Sharpe · drawdown · win rate only',
      'D3 status indicator',
      'Risk disclaimer on every page — cannot be dismissed',
      '── WHAT INNER CIRCLE NEVER GETS ────────────────────────────────',
      'Positions · P&L details · individual stock holdings',
      'L6 weights · conviction formula · any signal score values',
      'L3 reasoning text · execution controls of any kind',
      'Position sizes · Kelly fraction · portfolio allocation',
      '── MONETISATION MODEL (owner decides at trigger) ─────────────────',
      'Option A: Performance-based access fee (decided at trigger time)',
      'Option B: Invitation-only, no fee (trust-building phase)',
      'Commercial monetisation comes later as Priority 4 product',
    ],
    prereqs: [
      'D3-OWNER-PORTAL complete ✅',
      'D3 live 12+ months ✅',
      'Owner explicit approval ✅',
    ],
    doD: [
      'isD3Viewer role functional in portal ✅',
      'Scope enforcement server-side verified ✅',
      'At least one inner circle member onboarded ✅',
      'Monetisation model decided and documented ✅',
    ],
    smokeChecks: [],
    files: [],
    d3Signal: 'Inner circle conviction signal distribution — directional only, protected.',
  },


];  // end PIPELINE_SESSIONS
